import { useQuery } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';

const STALE_TIME = 10 * 60 * 1000;

/**
 * Fetches Cube.js schema metadata via the backend proxy (GET /cube/meta).
 *
 * @param {object} [params]
 * @param {string} [params.type]  - 'table' | 'dimension' | 'measure'
 * @param {string} [params.cubes] - comma-separated cube names (for dimension/measure)
 *
 * Returns { isLoading, error, response: { cubes: [...] } }
 * Drop-in replacement for useCubeMeta() — no CubeProvider needed.
 * No params = full meta (backward compatible).
 */
export default function useCubeMetaAPI({ type, cubes } = {}) {
  const okapiKy = useOkapiKy();

  const params = new URLSearchParams();
  if (type)  params.set('type', type);
  if (cubes) params.set('cubes', cubes);
  const queryString = params.toString();
  const url = queryString ? `cube/meta?${queryString}` : 'cube/meta';

  const { data, isLoading, error } = useQuery({
    queryKey: ['cube-meta', type || 'all', cubes || 'all'],
    queryFn: () => okapiKy.get(url).json(),
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    isLoading,
    error: error || null,
    response: data || null,
  };
}
